import type {
  CupaFragmentFragment,
  CupaInput,
  LepcPickerFragment,
} from "generated-graphql/graphql";

import { useMutation, useQuery } from "@apollo/client";
import { Box, Grid } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { CheckboxField } from "components/Forms/CheckboxField";
import { FormTextField } from "components/Forms/FormTextField";
import { LepcPickerOmnisearch } from "components/LepcPicker";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { AgencyDueDateFields } from "../AgencyDueDateFields";
import { CREATE_CUPA, GET_CUPA, UPDATE_CUPA } from "./schema";

type CupaDialogProps = {
  onClose: () => void;
  mode: "add" | "edit";
  cupaId?: string;
};

// Shared by the form's defaultValues (cupa not yet loaded) and values (cupa
// loaded/changed) so the two stay in sync as fields are added.
function cupaToFormValues(cupa?: CupaFragmentFragment | null): CupaInput {
  return {
    cupaCode: cupa?.cupaCode ?? "",
    displayName: cupa?.displayName ?? "",
    districtName: cupa?.districtName ?? "",
    county: cupa?.county ?? "",
    notes: cupa?.notes ?? "",
    lepcId: cupa?.lepcId ?? null,
    dueDate: cupa?.dueDate ?? null,
    dueDateIsAnniversary: cupa?.dueDateIsAnniversary ?? false,
    reportsMixturesAsEhs: cupa?.reportsMixturesAsEhs ?? false,
  };
}

export function CupaDialog({ onClose, mode, cupaId }: CupaDialogProps) {
  const alerts = useAlerts();

  const { data: cupaData, loading: isLoadingCupa } = useQuery(GET_CUPA, {
    fetchPolicy: "cache-and-network",
    variables: { id: cupaId ?? "" },
    skip: !cupaId,
  });

  // Refetch by operation name so the grid's active `cupas` query (which runs
  // with search/page/sort variables) is refreshed. A document-only descriptor
  // refetches with undefined variables, landing on a different cache key and
  // leaving the grid stale.
  const [createCupa, { loading: isCreating }] = useMutation(CREATE_CUPA, {
    refetchQueries: ["cupas"],
  });
  const [updateCupa, { loading: isUpdating }] = useMutation(UPDATE_CUPA, {
    refetchQueries: ["cupas"],
  });

  const cupa = cupaData?.cupa;

  const [selectedLepc, setSelectedLepc] = useState<LepcPickerFragment | null>(null);
  useEffect(() => {
    setSelectedLepc(cupa?.lepc ?? null);
  }, [cupa?.lepc]);

  const form = useForm<CupaInput>({
    mode: "onChange",
    defaultValues: cupaToFormValues(cupa),
    values: cupa ? cupaToFormValues(cupa) : undefined,
  });

  const { handleSubmit, control, getValues, watch, setValue, formState } = form;
  const savingOrLoading = (mode === "edit" && isLoadingCupa) || isCreating || isUpdating;

  const onSubmit = async () => {
    const values = getValues();
    const input = {
      ...values,
      dueDate: values.dueDateIsAnniversary ? null : values.dueDate || null,
    };
    if (mode === "add") {
      await createCupa({
        variables: { input },
        onCompleted: () => {
          onClose();
          alerts.success("CUPA created successfully");
        },
        onError: (error) => {
          alerts.error("Failed to create CUPA", error);
        },
      });
    } else if (cupaId) {
      await updateCupa({
        variables: { id: cupaId, input },
        onCompleted: () => {
          onClose();
          alerts.success("CUPA updated successfully");
        },
        onError: (error) => {
          alerts.error("Failed to update CUPA", error);
        },
      });
    }
  };

  const title = mode === "add" ? "Add CUPA" : "Edit CUPA";

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title={title}
      primaryActionButtonProps={{
        loading: savingOrLoading,
        onClick: handleSubmit(onSubmit),
        disabled: !formState.isValid,
      }}
    >
      <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: "100%" }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 4 }}>
            <FormTextField
              name="cupaCode"
              label="CUPA Code"
              control={control}
              rules={{ required: "CUPA code is required" }}
              textFieldProps={{ required: true }}
            />
          </Grid>
          <Grid size={{ xs: 8 }}>
            <FormTextField name="county" label="County" control={control} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormTextField
              name="displayName"
              label="Display Name"
              control={control}
              rules={{ required: "Display name is required" }}
              textFieldProps={{ required: true }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormTextField
              name="districtName"
              label="District Name"
              control={control}
              rules={{ required: "District name is required" }}
              textFieldProps={{ required: true }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <FormTextField name="notes" label="Notes" control={control} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Controller
              name="lepcId"
              control={control}
              render={({ field, fieldState }) => (
                <LepcPickerOmnisearch
                  {...fieldState}
                  label="LEPC"
                  displayAgencyLink
                  resultFilter={(lepc) => lepc.state === "CA"}
                  value={selectedLepc}
                  onChange={(value) => {
                    field.onChange(value?.id ?? null);
                    setSelectedLepc(value ?? null);
                  }}
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <AgencyDueDateFields control={control} watch={watch} setValue={setValue} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <CheckboxField
              name="reportsMixturesAsEhs"
              control={control}
              label="Mixtures are reported as EHS"
              tooltip="CERS guidance is to mark a mixture as not EHS and mark only its EHS components. Check this only if this CUPA has asked for the top-level EHS mark. Record who asked in Notes."
            />
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
}
